import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';

function SectorHeatmap({ allSectors, onSectorClick, getSaturationColor }) {
  const { t, i18n } = useTranslation();
  const [sortDir, setSortDir] = useState('desc');

  const sorted = [...allSectors].sort((a, b) =>
    sortDir === 'desc'
      ? b.combined_score - a.combined_score
      : a.combined_score - b.combined_score
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {t('saturation.heatmap.title', 'Bão Hòa Theo Ngành')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t('saturation.heatmap.subtitle', 'Nhấn vào ngành để xem chi tiết')}
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            value={sortDir}
            exclusive
            onChange={(e, v) => v && setSortDir(v)}
            sx={{ height: 30, '& .MuiToggleButton-root': { px: 0.75, py: 0.25 } }}
          >
            <ToggleButton value="desc">
              <ArrowDownwardIcon sx={{ fontSize: 15 }} />
            </ToggleButton>
            <ToggleButton value="asc">
              <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Sector rows */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {sorted.map((sector, idx) => {
            const name = i18n.language === 'vi' ? sector.sector.vi : sector.sector.en;
            const score = sector.combined_score;
            const info = getSaturationColor(score);

            return (
              <Box
                key={sector.sector.en}
                onClick={() => onSectorClick(sector)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  py: 1,
                  px: 1.25,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {/* Color dot */}
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: info.color, flexShrink: 0 }} />

                {/* Sector name */}
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.primary"
                  sx={{ width: { xs: 90, sm: 130, md: 150 }, flexShrink: 0, fontSize: '0.82rem' }}
                  noWrap
                  title={name}
                >
                  {name}
                </Typography>

                {/* Progress bar + Score */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box sx={{ flex: 1, height: 7, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        width: `${Math.min(score, 100)}%`,
                        height: '100%',
                        bgcolor: info.color,
                        borderRadius: 4,
                        transition: 'width 0.5s ease',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={info.color}
                    sx={{ width: 36, textAlign: 'right', fontFeatureSettings: '"tnum"', fontSize: '0.82rem' }}
                  >
                    {score.toFixed(1)}
                  </Typography>
                </Box>

                {/* Status chip — hidden on xs */}
                <Chip
                  label={info.text}
                  size="small"
                  sx={{
                    height: 21,
                    bgcolor: info.color + '12',
                    color: info.color,
                    fontWeight: 600,
                    fontSize: '0.66rem',
                    minWidth: 58,
                    '& .MuiChip-label': { px: 0.75 },
                    display: { xs: 'none', sm: 'flex' },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}

export default SectorHeatmap;
